import { useCallback, useMemo, useRef, useState, type RefObject } from 'react'
import { Camera, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { api } from '../../../../api/client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useActiveCanvasId } from '../../../../contexts/ActiveCanvasIdContext'
import { createNadDebug } from '../../../../utils/debugLog'
import type { ToolbarAction } from './AgentNodeToolbar'
import type { VideoPreviewPlayerHandle } from './VideoPreviewPlayer'

const extractFrameLog = createNadDebug('dw:extract-frame')

const EXTRACT_FRAME_LABEL_KEYS = {
  current: 'videoGeneratorNode.extractFrameCurrent',
  first: 'videoGeneratorNode.extractFrameFirst',
  last: 'videoGeneratorNode.extractFrameLast',
}
type ExtractFramePosition = keyof typeof EXTRACT_FRAME_LABEL_KEYS
// Camera dropdown order is part of the UX contract: Current, First, Last.
const EXTRACT_FRAME_POSITIONS: ExtractFramePosition[] = ['current', 'first', 'last']

export function useExtractFrameAction({ nodeId, hasVideo, videoPreviewRef }: {
  nodeId: string
  hasVideo: boolean
  videoPreviewRef: RefObject<VideoPreviewPlayerHandle>
}): ToolbarAction {
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  const canvasId = useActiveCanvasId()
  const [isExtractingFrame, setIsExtractingFrame] = useState(false)
  const inFlight = useRef(false)

  const extractFrame = useCallback(async (position: ExtractFramePosition) => {
    if (!projectId || !hasVideo || inFlight.current) return
    const currentTime = videoPreviewRef.current?.videoElement?.currentTime ?? 0
    const frame = position === 'current' ? `time:${currentTime.toFixed(3)}` : position

    inFlight.current = true
    setIsExtractingFrame(true)
    extractFrameLog({ event: 'extract-frame:start', project_id: projectId, canvas_id: canvasId, nodeId, position, frame })

    try {
      const { imageNodeId } = await api.extractVideoFrame(projectId, nodeId, canvasId, frame)
      extractFrameLog({ event: 'extract-frame:success', project_id: projectId, canvas_id: canvasId, nodeId, position, frame, imageNodeId })
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      extractFrameLog({ event: 'extract-frame:error', project_id: projectId, canvas_id: canvasId, nodeId, position, frame, error: message })
      toast.error(t('videoGeneratorNode.extractFrameFailed', { message }))
    } finally {
      inFlight.current = false
      setIsExtractingFrame(false)
    }
  }, [projectId, canvasId, nodeId, hasVideo, videoPreviewRef, t])

  return useMemo(() => ({
    key: 'extractFrame',
    label: t('videoGeneratorNode.extractFrame'),
    icon: isExtractingFrame
      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
      : <Camera className="w-3.5 h-3.5" />,
    disabled: !projectId || !hasVideo || isExtractingFrame,
    menuItems: EXTRACT_FRAME_POSITIONS.map((position) => ({
      key: `extract-frame-${position}`,
      label: t(EXTRACT_FRAME_LABEL_KEYS[position]),
      onClick: () => { void extractFrame(position) },
      disabled: !projectId || !hasVideo || isExtractingFrame,
    })),
  }), [projectId, hasVideo, isExtractingFrame, extractFrame, t])
}
